import { createBrowserRouter, Navigate, Outlet, useRouteError } from 'react-router';
import type { AdminPermission } from '@onov/contracts';
import { AdminShell } from './AdminShell.js';
import { useAuthStore } from '../lib/auth-store.js';
import { Login } from '../features/auth/Login.js';
import { Mfa } from '../features/auth/Mfa.js';
import { Dashboard } from '../features/dashboard/Dashboard.js';
import { lazy, Suspense } from 'react';

// Route-level code splitting: each section is its own chunk; auth + dashboard
// stay in the entry bundle for first paint.
const L = (f: () => Promise<{ [k: string]: React.ComponentType }>, name: string) => {
  const C = lazy(() => f().then((m) => ({ default: m[name]! })));
  return (
    <Suspense fallback={<div className="p-6 text-sm text-grey-500">Loading…</div>}>
      <C />
    </Suspense>
  );
};
const ReportQueue = () => L(() => import('../features/moderation/ReportQueue.js'), 'ReportQueue');
const Appeals = () => L(() => import('../features/moderation/Appeals.js'), 'Appeals');
const Members = () => L(() => import('../features/members/Members.js'), 'Members');
const UpgradeQueue = () => L(() => import('../features/standing/UpgradeQueue.js'), 'UpgradeQueue');
const Tiers = () => L(() => import('../features/standing/Tiers.js'), 'Tiers');
const Live = () => L(() => import('../features/live/Live.js'), 'Live');
const Payments = () => L(() => import('../features/commerce/Payments.js'), 'Payments');
const Tickets = () => L(() => import('../features/support/Tickets.js'), 'Tickets');
const Content = () => L(() => import('../features/content/Content.js'), 'Content');
const Templates = () => L(() => import('../features/content/Templates.js'), 'Templates');
const Broadcast = () => L(() => import('../features/content/Broadcast.js'), 'Broadcast');
const Audit = () => L(() => import('../features/platform/Audit.js'), 'Audit');
const Admins = () => L(() => import('../features/platform/Admins.js'), 'Admins');
const Roles = () => L(() => import('../features/platform/Roles.js'), 'Roles');

/**
 * Route-level PERMISSION gate — a convenience over the server's own 403
 * (AC-ADMGL-01). Permissions are the only authorization input: built-in and
 * custom roles alike arrive here as the permission set the API attached to
 * the session, so a role name is never consulted.
 */
function RequireAccess({ permission }: { permission: AdminPermission }) {
  const permissions = useAuthStore((s) => s.permissions);
  return permissions.includes(permission) ? <Outlet /> : <Navigate to="/" replace />;
}
function ErrorBoundary() {
  const e = useRouteError() as Error;
  return (
    <div className="rounded-lg border bg-white p-6">
      <h1 className="text-base font-semibold text-red-600">Something went wrong</h1>
      <pre className="mt-2 whitespace-pre-wrap text-xs text-grey-500">{e?.message}</pre>
    </div>
  );
}

export const router = createBrowserRouter([
  { path: '/login', element: <Login /> },
  { path: '/mfa', element: <Mfa /> },
  {
    path: '/',
    element: <AdminShell />,
    errorElement: <ErrorBoundary />,
    children: [
      { index: true, element: <Dashboard /> },
      {
        element: <RequireAccess permission="moderation:report:manage" />,
        children: [
          { path: 'moderation', element: <ReportQueue /> },
          { path: 'live', element: <Live /> },
        ],
      },
      {
        element: <RequireAccess permission="moderation:appeal:manage" />,
        children: [{ path: 'moderation/appeals', element: <Appeals /> }],
      },
      {
        element: <RequireAccess permission="member:read" />,
        children: [{ path: 'members', element: <Members /> }],
      },
      {
        element: <RequireAccess permission="standing:upgrade:manage" />,
        children: [{ path: 'standing', element: <UpgradeQueue /> }],
      },
      {
        element: <RequireAccess permission="standing:tier:manage" />,
        children: [{ path: 'standing/tiers', element: <Tiers /> }],
      },
      {
        element: <RequireAccess permission="content:policy:manage" />,
        children: [{ path: 'content', element: <Content /> }],
      },
      {
        element: <RequireAccess permission="notification:template:manage" />,
        children: [{ path: 'content/templates', element: <Templates /> }],
      },
      {
        element: <RequireAccess permission="notification:broadcast:send" />,
        children: [{ path: 'broadcast', element: <Broadcast /> }],
      },
      {
        element: <RequireAccess permission="platform:admin:manage" />,
        children: [{ path: 'platform/admins', element: <Admins /> }],
      },
      {
        element: <RequireAccess permission="platform:role:manage" />,
        children: [{ path: 'platform/roles', element: <Roles /> }],
      },
      {
        element: <RequireAccess permission="payment:read" />,
        children: [{ path: 'commerce', element: <Payments /> }],
      },
      {
        element: <RequireAccess permission="platform:audit:read" />,
        children: [{ path: 'platform', element: <Audit /> }],
      },
      {
        element: <RequireAccess permission="support:ticket:manage" />,
        children: [{ path: 'support', element: <Tickets /> }],
      },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);
